import React, { ReactEventHandler, useEffect, useRef, useState } from 'react';
import defaultBackgroundImage from '@/assets/images/default_dana.jpg';
import { Slide as SlideType } from '@/eventConfig/types.ts';
import { isNil } from 'ramda';
import { FCWW } from '../../../types.ts';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { useSlideAnimation } from '@/components/slides/useSlideAnimation.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { useForm } from '@/form/useForm.ts';
import { useUiEvent } from '@/util/useUiEvent.ts';

export interface SlideComponentProps {
  className?: string;
  slide: SlideType;
}

export const Slide: FCWW<SlideComponentProps> = ({ className, slide, children }) => {
  const { formData } = useForm();
  const { eventConfig } = useEventConfig();
  const { image, intro, title, subtitle, text } = slide;
  const { handleUiEvent } = useUiEvent();

  const slideAnimation = useSlideAnimation(slide.transition || 'slide', { enter: 600, exit: 300 });

  const slideRef = useRef<HTMLDivElement>(null);

  // Focus behavior for input fields
  const isHidden = (el: HTMLElement) => {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return true;
    // Consider elements that are detached or with zero dimensions as hidden
    const rect = el.getBoundingClientRect();
    return (rect.width === 0 && rect.height === 0) || el.hasAttribute('hidden');
  };

  const isFocusableField = (el: HTMLElement) => {
    const tag = el.tagName.toLowerCase();
    if (tag === 'input') {
      const type = (el as HTMLInputElement).type?.toLowerCase();
      if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset') return false;
    }
    if ((el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (isHidden(el)) return false;
    // focusable by default or via tabindex
    return el.tabIndex >= 0;
  };

  const shouldSkipEnterNav = (el: HTMLElement | null) => {
    if (!el) return false;
    return !!el.closest('[data-skip-enter-nav]');
  };

  const focusNextField = (current: HTMLElement) => {
    const container = slideRef.current ?? current.closest('.Slide');
    if (!container) return false;
    const candidates = Array.from(
      container.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]')
    ).filter(isFocusableField);

    if (candidates.length === 0) return false;

    const active = document.activeElement as HTMLElement | null;
    const index = Math.max(
      0,
      candidates.findIndex((el) => el === active || el.contains(active as Node))
    );
    const next = candidates[index + 1];
    if (next) {
      next.focus();
      // Put caret at the end for text-like inputs
      if (next.tagName.toLowerCase() === 'input') {
        try {
          const input = next as HTMLInputElement;
          const len = input.value?.length ?? 0;
          input.setSelectionRange?.(len, len);
        } catch (_) {
          // ignore
        }
      }
      return true;
    }
    return false;
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.defaultPrevented) return;
    const key = e.key;
    if (key !== 'Enter' && key !== 'NumpadEnter') return;

    const target = e.target as HTMLElement;
    const tag = target.tagName.toLowerCase();
    if (tag === 'textarea' || (target as any).isContentEditable) return; // allow newline in textarea/contenteditable
    if (shouldSkipEnterNav(target)) return;

    // Move focus to the next input field
    const moved = focusNextField(target);
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Fallback: trigger slide 'next' navigation if configured
      handleUiEvent?.({ type: 'nav', target: 'next' });
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    const container = slideRef.current;
    if (!container) return;

    const first = Array.from(
      container.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]')
    ).filter(isFocusableField)[0];

    if (first) {
      first.focus();
    } else {
      const nextButton = document.querySelector<HTMLElement>('.nav-next');
      nextButton?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (slide.enter) {
      window.setTimeout(
        () => {
          if (!isNil(slide.enter)) {
            handleUiEvent(slide.enter);
          }
        },
        (slide.enter.delay || 0) * 1000
      );
    }
  }, [handleUiEvent, slide.enter]);

  // Scroll indicator setup
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const slideEl = slideRef.current;
    const contentEl = contentRef.current;

    const threshold = -25;

    const compute = () => {
      let contentShows = false;
      if (contentEl) {
        const style = window.getComputedStyle(contentEl);
        const overflowY = style.overflowY;
        const canScrollContainer = overflowY === 'auto' || overflowY === 'scroll';
        const canScroll = contentEl.scrollHeight - contentEl.clientHeight > threshold;
        const atBottom = contentEl.scrollTop >= contentEl.scrollHeight - contentEl.clientHeight - threshold;
        contentShows = canScrollContainer && canScroll && !atBottom;
      }

      let pageShows = false;
      if (slideEl) {
        const rect = slideEl.getBoundingClientRect();
        // If bottom of slide is below viewport, there is more to scroll
        pageShows = rect.bottom - threshold > window.innerHeight;
      }

      setShowScrollHint(contentShows || pageShows);
    };

    // Initial compute and listeners
    compute();
    const onWindow = () => compute();
    const onContent = () => compute();

    window.addEventListener('resize', onWindow);
    window.addEventListener('scroll', onWindow, { passive: true } as any);
    if (contentEl) contentEl.addEventListener('scroll', onContent, { passive: true } as any);

    const moTargets: MutationObserver[] = [];
    if (slideEl) {
      const mo = new MutationObserver(() => compute());
      mo.observe(slideEl, { childList: true, subtree: true });
      moTargets.push(mo);
    }

    // Recompute after a tick to catch async content
    const id = window.setTimeout(compute, 50);

    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', onWindow);
      window.removeEventListener('scroll', onWindow as any);
      if (contentEl) contentEl.removeEventListener('scroll', onContent as any);
      moTargets.forEach((m) => m.disconnect());
    };
  }, [slide.id]);

  const handleImageError: ReactEventHandler<HTMLImageElement> = ({ currentTarget }) => {
    currentTarget.onerror = null;
    currentTarget.src = defaultBackgroundImage;
  };

  const templateData = {
    form: formData,
    eventConfig,
  };

  const slideClasses = `slide-${slide.id} ${className || ''}`;

  const getTextClasses = (type: 'intro' | 'subtitle' | 'text', textBoxType: any) => {
    if (!isNil(textBoxType) && typeof textBoxType === 'string') {
      return `${type} align-left columns-1`;
    }
    return `${type} align-${textBoxType?.align || 'left'} columns-${textBoxType?.columns || 1}`;
  };

  const introClasses = getTextClasses('intro', intro);
  const subtitleClasses = getTextClasses('subtitle', subtitle);
  const textClasses = getTextClasses('text', text);

  return (
    <div ref={slideRef} onKeyDown={handleKeyDown} className={`Slide h-100 w-100 ${slideClasses} ${slideAnimation}`}>
      {image && (
        <div className="bg-wrapper">
          <img alt="" height="100%" src={image} onError={handleImageError} />
        </div>
      )}
      <div className="header">
        {intro && <div className={introClasses}>{fromTemplate(intro, templateData)}</div>}
        {title && <h1 className="title">{fromTemplate(title, templateData)}</h1>}
        {subtitle && <div className={subtitleClasses}>{fromTemplate(subtitle, templateData)}</div>}
        {text && <div className={textClasses}>{fromTemplate(text, templateData)}</div>}
      </div>

      <div className="content" ref={contentRef}>
        {children}
      </div>

      <div className={`scroll-indicator ${showScrollHint ? 'visible' : ''}`} aria-hidden="true">
        <i className="fas fa-chevron-down chevron" />
      </div>
    </div>
  );
};
